import {
  BookOpen,
  CalendarDays,
  ChevronRight,
  Globe2,
  Instagram,
  MapPin,
  ShoppingBag,
  Wine,
} from "lucide-react";
import { useEffect, useState } from "react";
import { PageMeta } from "@/components/PageMeta";
import { SITE, trackEvent } from "@/lib/site";

const ORIGIN = "https://www.angkorflavours.com.au";
const PRODUCT_URL = `${ORIGIN}/products/angkor-amok-curry-paste-280g`;
const MENU_URL = `${ORIGIN}/menu`;
const EVENTS_URL = `${ORIGIN}/events`;

type LinksMap = {
  marvelFeast: string;
  product: string;
  bookTable: string;
  menu: string;
  events: string;
  delivery: string;
  pickup: string;
};

const baseLinks: LinksMap = {
  marvelFeast: SITE.booking,
  product: PRODUCT_URL,
  bookTable: SITE.booking,
  menu: MENU_URL,
  events: EVENTS_URL,
  delivery: SITE.uberEats,
  pickup: SITE.directOrder,
};

type LinkKey = keyof LinksMap;

const campaignContent: Record<LinkKey, string> = {
  marvelFeast: "book-table",
  product: "amok-paste",
  bookTable: "book-table",
  menu: "menu",
  events: "events",
  delivery: "delivery",
  pickup: "pickup",
};

function appendCampaignTracking(url: string, content: string) {
  if (typeof window === "undefined") return url;

  const incoming = new URLSearchParams(window.location.search);
  const source = incoming.get("utm_source");
  if (!source) return url;

  const tracked = new URL(url, window.location.origin);
  tracked.searchParams.set("utm_source", source);
  tracked.searchParams.set("utm_medium", incoming.get("utm_medium") || "social");
  tracked.searchParams.set(
    "utm_campaign",
    incoming.get("utm_campaign") || "linkinbio"
  );
  tracked.searchParams.set("utm_content", content);
  return tracked.toString();
}

function Chevron() {
  return <ChevronRight className="links-btn-chevron" aria-hidden="true" />;
}

export default function LinksPage() {
  const [links, setLinks] = useState<LinksMap>(baseLinks);

  useEffect(() => {
    setLinks(
      Object.fromEntries(
        Object.entries(baseLinks).map(([key, url]) => [
          key,
          appendCampaignTracking(url, campaignContent[key as LinkKey]),
        ])
      ) as Record<LinkKey, string>
    );

    let theme = document.head.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]'
    );
    const createdTheme = !theme;
    const previousTheme = theme?.content;
    if (!theme) {
      theme = document.createElement("meta");
      theme.name = "theme-color";
      document.head.appendChild(theme);
    }
    theme.content = "#8B2E1F";

    return () => {
      if (theme && createdTheme) theme.remove();
      else if (theme && previousTheme) theme.content = previousTheme;
    };
  }, []);

  const track = (content: string) =>
    trackEvent("links_page_click", { placement: content });

  return (
    <div className="links-page" id="main-content" tabIndex={-1}>
      <PageMeta
        title="ANGKOR Cambodian Flavours | Links"
        description="Book a table, view the menu, order Amok Curry Paste and plan your event at ANGKOR Cambodian Flavours Restaurant & Bar, 389 Spencer Street, West Melbourne."
        path="/links"
        image="/manus-storage/angkor-mark-a-crown-360_0d227cac.png"
        robots="noindex, follow"
      />

      <div className="links-band" aria-hidden="true" />
      <div className="links-page-inner">
        <header className="links-brand">
          <img
            className="links-brand-mark"
            src="/manus-storage/angkor-mark-a-crown-360_0d227cac.png"
            width="112"
            height="112"
            alt="ANGKOR logo"
          />
          <h1>ANGKOR</h1>
          <p className="links-brand-sub">Cambodian Flavours Restaurant &amp; Bar</p>
          <div className="links-ornament" aria-hidden="true">
            <span />
          </div>
          <p className="links-brand-line">Neri’s family recipes in West Melbourne</p>
          <p className="links-brand-location">
            <MapPin aria-hidden="true" />
            Near Southern Cross · West Melbourne
          </p>
        </header>

        <main>
          <nav aria-label="ANGKOR links">
            <ul className="links-list">
              <li className="links-feature-group">
                <a
                  className="links-btn links-btn-primary"
                  href={links.marvelFeast}
                  onClick={() => track("book-table")}
                >
                  <span className="links-btn-text">
                    Book a Table
                  </span>
                  <Chevron />
                </a>

                <a
                  className="links-btn links-btn-product"
                  href={links.product}
                  onClick={() => track("amok-paste")}
                >
                  <img
                    className="links-btn-thumb"
                    src="/manus-storage/amok-jar-warm-scene-thumb-240_f49747cf.jpg"
                    width="76"
                    height="76"
                    alt=""
                  />
                  <span className="links-btn-text">
                    Order Amok Curry Paste
                    <span className="links-btn-sub">
                      <span className="links-nowrap">280g jar · $15 ·</span>{" "}
                      <span className="links-nowrap">Ships Australia-wide</span>
                    </span>
                  </span>
                  <Chevron />
                </a>
              </li>

              <li>
                <a
                  className="links-btn links-btn-outline"
                  href={links.bookTable}
                  onClick={() => track("book-table")}
                >
                  <CalendarDays className="links-btn-icon" aria-hidden="true" />
                  <span className="links-btn-text">Book a table</span>
                  <Chevron />
                </a>
              </li>
              <li>
                <a
                  className="links-btn links-btn-outline"
                  href={links.menu}
                  onClick={() => track("menu")}
                >
                  <BookOpen className="links-btn-icon" aria-hidden="true" />
                  <span className="links-btn-text">View menu</span>
                  <Chevron />
                </a>
              </li>
              <li>
                <a
                  className="links-btn links-btn-outline"
                  href={links.events}
                  onClick={() => track("events")}
                >
                  <Wine className="links-btn-icon" aria-hidden="true" />
                  <span className="links-btn-text">Events &amp; functions</span>
                  <Chevron />
                </a>
              </li>
              <li>
                <a
                  className="links-btn links-btn-outline"
                  href={links.delivery}
                  onClick={() => track("delivery")}
                >
                  <ShoppingBag className="links-btn-icon" aria-hidden="true" />
                  <span className="links-btn-text">Order food delivery</span>
                  <Chevron />
                </a>
              </li>
              <li>
                <a
                  className="links-btn links-btn-outline"
                  href={links.pickup}
                  onClick={() => track("pickup")}
                >
                  <ShoppingBag className="links-btn-icon" aria-hidden="true" />
                  <span className="links-btn-text">
                    Order online for pickup
                    <span className="links-btn-sub">Buy Gift Cards online</span>
                  </span>
                  <Chevron />
                </a>
              </li>
            </ul>
          </nav>
        </main>

        <footer className="links-footer">
          <address>
            <ul className="links-footer-list">
              <li>
                <a className="links-footer-link" href={SITE.directions}>
                  <MapPin aria-hidden="true" />
                  <span>
                    389 Spencer Street,
                    <br />
                    West Melbourne VIC 3003
                  </span>
                </a>
              </li>
              <li>
                <a className="links-footer-link" href={SITE.instagram}>
                  <Instagram aria-hidden="true" />
                  <span>@angkor.21</span>
                </a>
              </li>
              <li>
                <a className="links-footer-link" href={`${ORIGIN}/`}>
                  <Globe2 aria-hidden="true" />
                  <span>www.angkorflavours.com.au</span>
                </a>
              </li>
            </ul>
          </address>
        </footer>
      </div>
    </div>
  );
}
